import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Target, Zap, TrendingUp, AlertCircle, Plus, X } from "lucide-react";
import Simulator from "@/components/Simulator";

export default function Radar() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [missions, setMissions] = useState([]);
  const makeExpense = () => ({ _id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()), name: "", category: "", amount: "" });
  const [expenses, setExpenses] = useState([makeExpense(), makeExpense(), makeExpense()]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [tab, setTab] = useState("missoes");
  const [resetAt, setResetAt] = useState(null);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    (async () => {
      try {
        await api.post("/stats/checkin");
        const [s, m] = await Promise.all([api.get("/stats"), api.get("/missions/today")]);
        setStats(s.data);
        setMissions(m.data.missions || m.data);
        if (m.data.next_reset_utc) setResetAt(new Date(m.data.next_reset_utc));
      } catch (err) { console.warn("[Radar] stats/missions load failed:", err?.message); }
    })();
  }, [user, loading, nav]);

  useEffect(() => {
    if (!resetAt) return;
    const tick = () => {
      const diff = resetAt.getTime() - Date.now();
      if (diff <= 0) { setCountdown("00:00:00"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resetAt]);

  const completeMission = async (id, xp) => {
    try {
      await api.post(`/missions/${id}/complete`);
      setMissions((ms) => ms.map((x) => x.id === id ? { ...x, done: true } : x));
      const { data } = await api.get("/stats");
      setStats(data);
      toast.success(`+${xp} XP!`);
    } catch { toast.error("Falhou, tenta de novo"); }
  };

  const analyzeRadar = async () => {
    if (!user.subscription_active) {
      toast.error("Assina pra usar o Radar");
      nav("/dashboard");
      return;
    }
    setAnalyzing(true);
    try {
      const cleaned = validExpenses();
      if (cleaned.length === 0) {
        toast.error("Preenche pelo menos 1 gasto com nome e valor");
        setAnalyzing(false);
        return;
      }
      const { data } = await api.post("/radar/analyze", { expenses: cleaned });
      setAnalysis(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na análise");
    } finally { setAnalyzing(false); }
  };

  const updateExpense = (i, key, val) => {
    setExpenses(exps => exps.map((e, idx) => idx === i ? { ...e, [key]: val } : e));
  };

  const validExpenses = () => expenses
    .map(e => ({ ...e, amount: parseFloat(String(e.amount).replace(",", ".")) || 0 }))
    .filter(e => e.name.trim() && e.amount > 0);

  if (!user) return null;

  return (
    <div data-testid="radar-page" className="grid-bg min-h-[85vh]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="sticker">TEU CORRE DIÁRIO</span>
        <h1 className="mt-6 font-bebas text-6xl md:text-7xl tracking-tighter">
          MISSÃO <span className="text-[#CCFF00]">GRANA</span>
        </h1>

        {/* XP Bar */}
        {stats && (
          <div data-testid="xp-bar" className="mt-8 brutal-card">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="text-white/50 font-manrope text-xs uppercase tracking-widest">Nível</div>
                <div className="font-bebas text-5xl text-[#CCFF00]">{stats.level}</div>
              </div>
              <div>
                <div className="text-white/50 font-manrope text-xs uppercase tracking-widest">XP</div>
                <div className="font-bebas text-5xl">{stats.xp}</div>
              </div>
              <div>
                <div className="text-white/50 font-manrope text-xs uppercase tracking-widest">Streak</div>
                <div className="font-bebas text-5xl text-[#FF0055] flex items-center gap-2"><Flame size={32}/> {stats.streak_days}d</div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-3 border-b-2 border-white/20">
          {[
            {k:"missoes", label:"Missões", icon: <Target size={16}/>},
            {k:"radar", label:"Radar de Vazamento", icon: <AlertCircle size={16}/>},
            {k:"simulador", label:"Simulador", icon: <TrendingUp size={16}/>},
          ].map(t => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={()=>setTab(t.k)}
              className={`px-4 py-3 font-bebas text-xl tracking-wide flex items-center gap-2 ${tab===t.k ? "text-[#CCFF00] border-b-4 border-[#CCFF00] -mb-[2px]" : "text-white/60"}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "missoes" && (
          <>
            {countdown && (
              <div data-testid="mission-countdown" className="mt-6 brutal-card flex flex-wrap items-center justify-between gap-4" style={{borderColor:'#00F0FF'}}>
                <div>
                  <div className="font-manrope text-xs uppercase tracking-widest text-white/50">Novas missões em</div>
                  <div className="font-bebas text-4xl md:text-5xl text-[#00F0FF] mt-1 tabular-nums">{countdown}</div>
                </div>
                <div className="text-right">
                  <div className="font-manrope text-xs uppercase tracking-widest text-white/50">Reset diário</div>
                  <div className="font-bebas text-2xl">18:00 BRT</div>
                </div>
              </div>
            )}
            <div className="mt-6 grid md:grid-cols-3 gap-6">
              {missions.map((m, i) => (
                <div key={m.id} data-testid={`mission-${m.id}`} className="brutal-card" style={{borderColor: m.done ? '#CCFF00' : '#333'}}>
                  <div className="flex items-start justify-between">
                    <Zap className={m.done ? "text-[#CCFF00]" : "text-white/50"} size={24}/>
                    <span className="font-bebas text-lg text-[#CCFF00]">+{m.xp} XP</span>
                  </div>
                  <h3 className="mt-4 font-bebas text-2xl tracking-tighter">{m.title}</h3>
                  <p className="mt-2 text-white/60 font-manrope text-sm">{m.desc}</p>
                  <button
                    data-testid={`mission-btn-${m.id}`}
                    disabled={m.done}
                    onClick={() => completeMission(m.id, m.xp)}
                    className="brutal-btn mt-6 w-full"
                    style={m.done ? { background: '#333', color: '#666', boxShadow: 'none' } : {}}
                  >
                    {m.done ? "✓ Feito" : "Fazer agora"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "radar" && (
          <div className="mt-8 grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5 brutal-card">
              <h2 className="font-bebas text-3xl tracking-tighter">Coloca teus gastos</h2>
              <p className="text-white/60 font-manrope text-sm mt-1">A IA vai apontar onde tua grana está vazando.</p>
              <div className="mt-6 space-y-3">
                {expenses.map((e, i) => (
                  <div key={e._id} className="flex gap-2 items-center">
                    <input data-testid={`exp-name-${i}`} value={e.name} onChange={ev=>updateExpense(i,'name',ev.target.value)} placeholder="Ex: iFood" className="brutal-input flex-1" style={{padding:'0.5rem'}}/>
                    <input data-testid={`exp-amount-${i}`} type="text" inputMode="decimal" value={e.amount} onChange={ev=>updateExpense(i,'amount',ev.target.value)} placeholder="R$" className="brutal-input w-24" style={{padding:'0.5rem'}}/>
                    <button onClick={()=>setExpenses(exps=>exps.filter((_,idx)=>idx!==i))} className="text-[#FF0055]"><X size={20}/></button>
                  </div>
                ))}
              </div>
              <button data-testid="add-expense" onClick={()=>setExpenses([...expenses, makeExpense()])} className="mt-4 flex items-center gap-2 text-[#CCFF00] font-manrope text-sm">
                <Plus size={16}/> Adicionar gasto
              </button>
              <button data-testid="analyze-radar" onClick={analyzeRadar} disabled={analyzing} className="brutal-btn w-full mt-6">
                {analyzing ? "Analisando..." : "Rodar Radar com IA"}
              </button>
            </div>

            <div className="md:col-span-7">
              {!analysis && !analyzing && (
                <div className="brutal-card border-dashed" style={{borderColor:'#333'}}>
                  <p className="text-white/50 font-manrope">Roda o Radar pra ver o diagnóstico completo.</p>
                </div>
              )}
              {analyzing && (
                <div className="brutal-card">
                  <p className="text-white/70 font-manrope">Analisando teus gastos com IA...</p>
                </div>
              )}
              {analysis && (
                <div data-testid="radar-result" className="space-y-4">
                  <div className="brutal-card brutal-shadow" style={{borderColor:'#CCFF00'}}>
                    <span className="sticker">DIAGNÓSTICO</span>
                    <p className="mt-4 font-manrope text-white/90 text-lg">{analysis.diagnostico}</p>
                    {analysis.economia_potencial_mensal > 0 && (
                      <div className="mt-6 flex items-baseline gap-2">
                        <span className="font-manrope text-sm text-white/60">Podia economizar:</span>
                        <span className="font-bebas text-4xl text-[#CCFF00]">R$ {analysis.economia_potencial_mensal}</span>
                        <span className="font-manrope text-sm text-white/60">/mês</span>
                      </div>
                    )}
                  </div>
                  {analysis.vazamentos?.map((v, i) => (
                    <div key={i} className="brutal-card" style={{borderColor: v.tag==='SANGRIA'?'#FF0055':v.tag==='VAZAMENTO'?'#00F0FF':'#333'}}>
                      <div className="flex justify-between items-start">
                        <h4 className="font-bebas text-2xl tracking-tighter">{v.nome}</h4>
                        <span className="font-bebas text-xs px-2 py-1 border-2" style={{color: v.tag==='SANGRIA'?'#FF0055':v.tag==='VAZAMENTO'?'#00F0FF':'#CCFF00', borderColor:'currentColor'}}>{v.tag}</span>
                      </div>
                      <p className="text-white/70 font-manrope text-sm mt-2">{v.comentario}</p>
                      <div className="mt-3 flex gap-4 font-manrope text-xs text-white/50">
                        <span>Mensal: <b className="text-white">R$ {v.valor_mensal}</b></span>
                        <span>Anual: <b className="text-white">R$ {v.valor_anual}</b></span>
                      </div>
                    </div>
                  ))}
                  {analysis.acao_urgente && (
                    <div className="brutal-card" style={{borderColor:'#FF0055'}}>
                      <span className="sticker">FAZ HOJE</span>
                      <p className="mt-4 font-manrope text-white/90">{analysis.acao_urgente}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "simulador" && (
          <div className="mt-8"><Simulator/></div>
        )}
      </div>
    </div>
  );
}
